import {Component, effect, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {jobTaskIcon, jobIcon} from "@common/helpers/jobs"
import {jobTaskStateColor, jobStateColor} from "@common/helpers/jobs/color"
import {JobTaskState, JobState} from "@generated"
import {ExecutionPlanNodeType} from "@platform/components/jobs/execution-plan/graph/types"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-execution-plan-tooltip",
    templateUrl: "execution-plan-tooltip.component.html",
    styleUrls: ["execution-plan-tooltip.component.sass"],
})
export class ExecutionPlanTooltipComponent {
    readonly $node = input<ExecutionPlanNodeType | undefined>(undefined, {alias: "node"})

    readonly $title = signal<string | undefined>(undefined)
    readonly $infoText = signal<string | undefined>(undefined)
    readonly $taskType = signal<string | undefined>(undefined)
    readonly $taskState = signal<JobTaskState | undefined>(undefined)
    readonly $externalJobState = signal<JobState | undefined>(undefined)
    readonly $inputLength = signal<number | undefined>(undefined)
    readonly $outputLength = signal<number | undefined>(undefined)
    readonly $progress = signal<number | undefined>(undefined)
    readonly $errorText = signal<string | undefined>(undefined)
    public message?: string

    readonly $isWaiting = signal<boolean | undefined>(undefined)

    constructor() {
        effect(() => {
            this.update()
        })
    }

    update() {
        this.reset()

        const node = this.$node()

        if (!node) {
            this.$title.set("Missing node")
            return
        }

        this.$inputLength.set(node.inputLength)
        this.$outputLength.set(node.outputLength)
        this.$errorText.set(node.error)

        switch (node.type) {
            case "task": {
                this.$title.set("Task node")
                this.$taskType.set(node.taskType)
                this.$taskState.set(node.dbState)
                this.$progress.set(node.progress ? Math.round(node.progress) : undefined)
                break
            }
            case "externalJob": {
                this.$title.set("External job node")
                this.$externalJobState.set(node.dbState)
                this.$progress.set(node.progress ? Math.round(node.progress) : undefined)
                break
            }
            case "progressGroup": {
                this.$title.set("Progress group node")
                this.$taskState.set(node.dbState)
                this.$progress.set(node.progress ? Math.round(node.progress) : undefined)
                break
            }
            case "jobDefinition": {
                this.$title.set("Job definition node")
                this.$taskState.set(node.dbState)
                this.$progress.set(node.progress ? Math.round(node.progress) : undefined)
                break
            }
            case "get": {
                this.$title.set("Get node")
                break
            }
            case "value": {
                this.$title.set("Value node")
                break
            }
            case "input": {
                this.$title.set("Input node")
                break
            }
            case "struct": {
                this.$title.set("Struct node")
                break
            }
            case "list": {
                this.$title.set("List node")
                break
            }
        }
    }

    reset() {
        this.$taskType.set(undefined)
        this.$infoText.set(undefined)
        this.$taskType.set(undefined)
        this.$taskState.set(undefined)
        this.$externalJobState.set(undefined)
        this.$inputLength.set(undefined)
        this.$outputLength.set(undefined)
        this.$progress.set(undefined)
        this.$errorText.set(undefined)
        this.message = undefined
        this.$isWaiting.set(undefined)
    }

    displayTaskState(state: JobTaskState) {
        switch (state) {
            case JobTaskState.Missing:
                return "Not yet created"
            default:
                return state
        }
    }

    displayExternalJobState(state: JobState) {
        switch (state) {
            case JobState.Running:
                return "In progress"
            case JobState.Complete:
                return "Completed"
            case JobState.Cancelled:
                return "Cancelled"
            case JobState.Failed:
                return "Failed"
            case JobState.Missing:
                return "Missing"
            default:
                return state
        }
    }

    public readonly jobTaskIcon = jobTaskIcon
    public readonly jobIcon = jobIcon
    protected readonly jobTaskStateColor = jobTaskStateColor
    protected readonly jobStateColor = jobStateColor
}
